// The site's dictionary. Every word here is marked wherever it occurs — in the
// commentary and in the ESV text beside it — and shows its definition when
// hovered or tapped (glossary/TermPopover.tsx). Like the bibliography, it is
// the published page that the admin edits in place.
import { renderMarkdown, type GlossaryEntryInput, type GlossaryEntryView } from "@bibleunleashed/shared";
import { useCallback, useEffect, useState } from "react";
import { BottomBar } from "@/components/BottomBar";
import { Field } from "@/components/Field";
import { Header } from "@/components/Header";
import { PageText } from "@/components/PageText";
import { Icon } from "@/components/Icon";
import { useUser } from "@/lib/auth";
import { useGlossary, useSaveGlossary } from "@/lib/glossary";

/** An entry being edited. Other forms are typed as one comma-separated line. */
interface DraftEntry {
  key: string;
  term: string;
  forms: string;
  definitionMd: string;
}

const toDraft = (e: GlossaryEntryView): DraftEntry => ({
  key: e.id,
  term: e.term,
  forms: e.forms.join(", "),
  definitionMd: e.definitionMd,
});

const toInput = (d: DraftEntry): GlossaryEntryInput => ({
  term: d.term.trim(),
  forms: d.forms
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean),
  definitionMd: d.definitionMd,
});

export function Glossary() {
  const { data: user } = useUser();
  const isAdmin = user?.role === "admin";
  const { data: entries, isLoading, error } = useGlossary();
  const save = useSaveGlossary();
  const [draft, setDraft] = useState<DraftEntry[] | null>(null);

  useEffect(() => {
    document.title = "Glossary · The Bible Unleashed";
  }, []);

  // "In the glossary" in a word's popover lands on its entry, once the list is in.
  useEffect(() => {
    const id = decodeURIComponent(location.hash.slice(1));
    if (id && entries) document.getElementById(id)?.scrollIntoView({ block: "start" });
  }, [entries]);

  const patch = useCallback(
    (index: number, change: Partial<DraftEntry>) =>
      setDraft((d) => (d ? d.map((e, i) => (i === index ? { ...e, ...change } : e)) : d)),
    [],
  );

  const doSave = async () => {
    if (!draft) return;
    // A row added and never filled in is not an entry with no word.
    const kept = draft.filter((d) => d.term.trim() || d.definitionMd.trim());
    await save.mutateAsync({ entries: kept.map(toInput) });
    setDraft(null);
  };

  const count = (draft ?? entries ?? []).length;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[760px] px-6 py-16 pb-28">
        <PageText id="glossary" titleClassName="font-serif text-[38px] font-semibold tracking-[-0.015em]" bodyClassName="mt-3 font-serif text-lg" />

        {isLoading && <p className="mt-10 font-serif text-ink-3">Loading…</p>}
        {error && <p className="mt-10 font-serif text-ink-2">Couldn’t load the glossary: {error.message}</p>}
        {!isLoading && !error && count === 0 && <p className="mt-10 font-serif text-ink-3">Nothing here yet.</p>}

        {draft ? (
          <ul className="mt-10 space-y-4">
            {draft.map((e, i) => (
              <li key={e.key} className="border-b border-rule pb-4 last:border-0">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Field label="Word" value={e.term} onChange={(term) => patch(i, { term })} />
                  <Field
                    label="Also matches"
                    value={e.forms}
                    placeholder="Plurals, other spellings — separated by commas"
                    onChange={(forms) => patch(i, { forms })}
                  />
                </div>
                <label className="mt-2 block">
                  <span className="mb-0.5 block font-sans text-[11px] tracking-[0.06em] text-ink-3 uppercase">Definition</span>
                  <textarea
                    value={e.definitionMd}
                    rows={3}
                    onChange={(ev) => patch(i, { definitionMd: ev.target.value })}
                    className="block w-full resize-y rounded border border-rule bg-paper px-2 py-1.5 font-serif text-[15px] leading-relaxed text-ink focus:border-accent focus-visible:outline-none"
                  />
                </label>
                <div className="mt-1 flex justify-end">
                  <button
                    onClick={() => setDraft((d) => (d ? d.filter((_, j) => j !== i) : d))}
                    aria-label={`Delete ${e.term || "this entry"}`}
                    className="grid size-7 place-items-center rounded text-ink-3 hover:bg-paper-3 hover:text-ink"
                  >
                    <Icon name="trash" width="15" height="15" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <dl className="mt-10">
            {(entries ?? []).map((e) => (
              <div key={e.id} id={e.id} className="glossary-entry -mx-3 scroll-mt-20 rounded-md border-b border-rule px-3 py-4 last:border-0">
                <dt className="font-serif text-[20px] font-semibold text-ink">
                  {e.term}
                  {e.forms.length > 0 && (
                    <span className="ml-2 font-sans text-[12px] font-normal text-ink-3">also {e.forms.join(", ")}</span>
                  )}
                </dt>
                {/* Rendered without marking: an entry would underline its own word. */}
                <dd
                  className="prose mt-1 font-serif text-[17px] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(e.definitionMd) }}
                />
              </div>
            ))}
          </dl>
        )}

        {draft && (
          <button
            onClick={() =>
              setDraft((d) => [...(d ?? []), { key: `new-${Date.now().toString(36)}`, term: "", forms: "", definitionMd: "" }])
            }
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-rule py-3 text-[13px] text-ink-2 hover:border-ink-3 hover:text-ink"
          >
            <Icon name="plus" width="15" height="15" /> Add a word
          </button>
        )}
      </main>

      {isAdmin && (
        <BottomBar>
          <div className="mx-auto flex max-w-[760px] items-center gap-2 px-6 py-2.5">
            <span className="font-sans text-[11px] tracking-[0.08em] text-ink-3 uppercase">
              {count} {count === 1 ? "word" : "words"}
            </span>
            {save.error && <span className="text-[12px] text-accent">{save.error.message}</span>}
            <span className="ml-auto flex gap-2">
              {draft ? (
                <>
                  <button onClick={() => setDraft(null)} className="rounded-md border border-rule px-3 py-1.5 text-[13px] text-ink-2 hover:text-ink">
                    Cancel
                  </button>
                  <button
                    onClick={() => void doSave()}
                    disabled={save.isPending}
                    className="rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
                  >
                    {save.isPending ? "Saving…" : "Save"}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setDraft((entries ?? []).map(toDraft))}
                  className="flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-white"
                >
                  <Icon name="pencil" width="15" height="15" /> Edit
                </button>
              )}
            </span>
          </div>
        </BottomBar>
      )}
    </div>
  );
}
